<template>
  <div>
    <!-- Обложка «на всю ширину экрана» — ДО контейнера: колонка контента режет всё,
         что вылезает за её границы (overflow: hidden), поэтому растянуть обложку
         отрицательными отступами изнутри нельзя. Вариант «в контейнере» (дефолт)
         рендерится на своём прежнем месте, внутри колонки контента. -->
    <SiteCover v-if="coverFullBleed" />

  <UContainer
    :ui="{
      base: 'mx-auto relative',
      padding: 'px-4 sm:px-6 lg:px-8',
      constrained: 'max-w-[1920px]',
    }"
  >
      <UiPullToRefreshIndicator
        :is-refreshing="isRefreshing"
        :is-pulling="isPulling"
        :pull-distance="pullDistance"
        :can-refresh="canRefresh"
      />
    <div class="grid-container">
      <AsideMenu/>
      <div class="grid-container__content">

        <!-- Заголовок страницы. Обложка со своим заголовком уже ставит h1 — тогда
             дублировать его не нужно, иначе на главной вообще нет h1: каталог
             начинается сразу с h2 категорий.

             Заголовок видимый, а не sr-only: скрытый текст поисковики учитывают
             неохотно (и по документации Яндекса он повод для недоверия), а
             гостю строка «что это за заведение и где» на первом экране нужна
             ровно так же, как роботу. -->
        <h1 v-if="pageHeading" class="page-heading">{{ pageHeading }}</h1>

        <!-- Обложка. По умолчанию выключена — сайт открывается сразу каталогом,
             как и раньше. Включается в админке: «Внешний вид → Обложка».
             Ширина «на всю страницу» рендерится выше, вне контейнера. -->
        <SiteCover v-if="!coverFullBleed" />

        <NewsSliderBanner/>
        <CurrentOrderStatus />

        <!-- «Вы заказывали»: повтор последнего заказа + товары по популярности -->
        <OrderedProductsCarousel />

        <UiLoyaltyInfo v-if="!storeAuth.isLoggedIn"/>

        <!-- Пустое состояние при активных фильтрах без результатов -->
        <UiEmptyFilterResults
          v-if="storeInstitution.hasActiveTagFilters && storeInstitution.totalProductsCount === 0"
          @reset="storeInstitution.selectedTagIds = []"
        />

        <!-- Двухуровневый каталог: первый уровень — плитка категорий -->
        <CategoryGrid
          v-if="showCategoryGrid"
          :categories="safeProducts"
          :config="catalogAppearance"
          @select="openCategory"
        />

        <!-- Второй уровень: шапка открытой категории с возвратом к плитке -->
        <div v-if="isTwoLevel && openedCategory" class="category-back">
          <button type="button" class="category-back__button" @click="backToCategories">
            <UIcon name="i-heroicons-arrow-left-20-solid" class="w-5 h-5" />
            <span>Все категории</span>
          </button>
        </div>

        <!-- Список товаров - показываем только если есть категории -->
        <template v-if="visibleCategories.length > 0">
          <template
            v-for="(category, index) in visibleCategories"
            :key="`category-wrapper-${index}-${category?.to || index}-${storeInstitution.selectedTagIds.join(',')}`"
          >
            <ProductCatalogSection
              v-if="category && category.products && Array.isArray(category.products) && category.products.length > 0"
              :id="category?.to || `category-${index}`"
              :key="`dish-${index}-${category?.to || index}-${storeInstitution.selectedTagIds.join(',')}`"
              :eager-media="index === 0"
              :title="category?.category || ''"
              :slug="category?.to || ''"
              :items="category.products"
            />
          </template>
        </template>

      </div>
      <BasketMobile/>

      <!-- Промо приложения — отдельной строкой грида, по ширине КОНТЕНТ-колонки
           (как товары выше), не на всю ширину. Своя строка нужна, чтобы липкая
           корзина (row 1) не перекрывала промо. Контент-колонка: xl → col 1
           (aside скрыт), 2xl → col 2 (aside 220px виден).
           Класс home-promo — якорь для пресетов раскладки: они двигают эти же
           пороги (assets/css/appearance-layout.scss). -->
      <AppWebPromoSection class="home-promo xl:col-start-1 xl:col-end-2 2xl:col-start-2 2xl:col-end-3" />
    </div>
  </UContainer>
  </div>
</template>
<script setup lang="ts">
import { consola } from 'consola'
import { useAppearance, useInstitutionStore, getReadyInstitutionId } from '@entities/institution'
import { useUserProfileStore } from '@entities/user'
import { usePullToRefresh } from '@features/app-shell'
import OrderedProductsCarousel from '~/features/order-history/ui/OrderedProductsCarousel.vue'
import { useAuthStore } from '~/features/auth'
import { coverRendersHeading, coverIsFullBleed } from '~/widgets/site-cover'

const logger = consola.withTag('index')

const storeAuth = useAuthStore();
const storeInstitution = useInstitutionStore();
const storeUserProfile = useUserProfileStore();

// ✅ Pull-to-Refresh
const { isRefreshing, isPulling, pullDistance, canRefresh } = usePullToRefresh(async () => {
  logger.info('Pull-to-refresh triggered')

  try {
    const institutionID = await getReadyInstitutionId();
    await storeInstitution.fetchBranches(institutionID);

    if (storeInstitution.selectedBranch?.id) {
      await storeInstitution.fetchProducts(
        storeInstitution.selectedBranch.id,
        storeUserProfile.getUserId || undefined,
      );
    }

    await storeUserProfile.fetchUser();
    logger.success('Pull-to-refresh completed')
  } catch (error) {
    logger.error('Pull-to-refresh error:', error)
    throw error;
  }
}, {
  threshold: 80,
  maxPullDistance: 150,
  hapticFeedback: true,
});

const safeProducts = computed(() => storeInstitution.products || []);

// ===== Двухуровневый каталог =====
// Выбранная категория живёт в адресе (?c=slug), а не в локальном состоянии:
// так работают «назад» в браузере и на телефоне, и ссылкой на категорию можно поделиться.
const route = useRoute();
const router = useRouter();
const { appearance, surface, catalog: catalogAppearance } = useAppearance();

/**
 * Заголовок главной — целиком из админки (SEO → «Заголовок на главной»,
 * `seo_meta.page_heading`).
 *
 * Каталог начинается с h2 категорий, и без этой строки на главной нет ни одного
 * h1 — поисковику нечем понять, о чём страница, кроме <title>. Но пустой или
 * навязанный h1 хуже отсутствующего, поэтому строку задаёт владелец, а не код:
 * раньше она собиралась здесь («{название} — меню и доставка еды, {город}») и
 * не правилась без выката.
 *
 * Пусто — заголовка нет вовсе. Так же и когда обложка уже вывела свой: два h1
 * хуже одного.
 */
const pageHeading = computed(() => {
  if (coverRendersHeading(appearance.value, surface.value)) return '';

  return String(storeInstitution.institution?.seo_meta?.page_heading ?? '').trim();
});

/**
 * Обложка «на всю ширину экрана» рендерится вне контейнера, а не в колонке контента.
 * Отсюда — два взаимоисключающих места для <SiteCover> в шаблоне.
 */
const coverFullBleed = computed(() => coverIsFullBleed(appearance.value, surface.value));

const isTwoLevel = computed(() => catalogAppearance.value.nesting === 'two-level');

const openedCategorySlug = computed(() => {
  const raw = route.query.c;
  return typeof raw === 'string' && raw ? raw : '';
});

const openedCategory = computed(() => {
  if (!openedCategorySlug.value) return null;
  return safeProducts.value.find((category: any) => category?.to === openedCategorySlug.value) || null;
});

/**
 * Что показываем в двухуровневом режиме:
 * ничего не выбрано → плитка категорий, выбрана → только её товары.
 * Поиск и фильтры по тегам сбрасывают вложенность: гость ищет по всему меню,
 * а не внутри одной категории, иначе результат выглядел бы пустым без причины.
 */
const isBrowsingAllCategories = computed(
  () => Boolean(storeInstitution.searchQuery) || storeInstitution.hasActiveTagFilters,
);

const showCategoryGrid = computed(
  () => isTwoLevel.value && !openedCategory.value && !isBrowsingAllCategories.value,
);

const visibleCategories = computed(() => {
  if (!isTwoLevel.value || isBrowsingAllCategories.value) return safeProducts.value;
  return openedCategory.value ? [openedCategory.value] : [];
});

const openCategory = (category: any) => {
  router.push({ query: { ...route.query, c: category?.to || undefined } });
};

const backToCategories = () => {
  const query = { ...route.query };
  delete query.c;
  router.push({ query });
};

// Категория пропала из меню (выключили в админке, сменили филиал) — возвращаем
// гостя к плитке, иначе он смотрит на пустой экран без объяснений.
watch(
  [openedCategorySlug, safeProducts],
  ([slug, categories]) => {
    if (!slug || !isTwoLevel.value || categories.length === 0) return;
    if (!categories.some((category: any) => category?.to === slug)) backToCategories();
  },
);

// SEO (title/description/og/canonical/JSON-LD) задаётся централизованно в
// layouts/default.vue из storeInstitution.institution.seo_meta — здесь дубль убран.

</script>

<style scoped lang="scss">
// Заголовок главной. Крупнее подписи, но заметно скромнее названий категорий
// (text-2xl/3xl): он представляет страницу, а не спорит с каталогом за внимание.
.page-heading {
  @apply pt-4 md:pt-6 text-lg md:text-xl font-semibold text-gray-700 dark:text-gray-200;
  font-family: var(--app-brand-font, inherit);
}

// Возврат к плитке категорий во втором уровне каталога
.category-back {
  @apply pt-8 md:pt-12;

  &__button {
    @apply inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold;
    @apply text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-zinc-800;
    @apply transition-colors hover:bg-gray-200 dark:hover:bg-zinc-700;
    border-radius: var(--app-radius-control, 9999px);
  }
}

.grid-container{
  @apply grid xl:grid-cols-[1fr_350px] 2xl:grid-cols-[220px_1fr_350px] gap-5 relative ;

  // Ширину колонки корзины читают пресеты раскладки (assets/css/appearance-layout.scss):
  // на главной она 350px, в избранном 320px — одно число на оба места не годится
  --app-basket-col: 350px;

  &__content{
    @apply overflow-hidden;
  }

  --swiper-scrollbar-bg-color: rgba(0, 0, 0, 0.1);
  --swiper-scrollbar-drag-bg-color: red;
}
</style>